/**
 * Strings of the notifications.
 * - The notification settings (2026-10-29): the server, category and
 *   channel menus (Mute, Notification settings), the channel header's bell,
 *   and the server's Notification settings dialog. Discord's words.
 * - The OS notifications (2026-11-01): the toasts' texts, the in-app prompt
 *   before the browser's permission prompt, and the device switches in
 *   Settings > Notifications.
 */
import type { Dict } from './core';

export const notifyEn: Dict = {
  // levels
  'nt.all': 'All messages',
  'nt.mentions': 'Only @mentions',
  'nt.nothing': 'Nothing',
  'nt.useServer': 'Use server default',
  'nt.useCategory': 'Use category default',
  // mute
  'nt.muteServer': 'Mute server',
  'nt.unmuteServer': 'Unmute server',
  'nt.muteCategory': 'Mute category',
  'nt.unmuteCategory': 'Unmute category',
  'nt.muteChannel': 'Mute channel',
  'nt.unmuteChannel': 'Unmute channel',
  'nt.dur.15m': 'For 15 minutes',
  'nt.dur.1h': 'For 1 hour',
  'nt.dur.3h': 'For 3 hours',
  'nt.dur.8h': 'For 8 hours',
  'nt.dur.24h': 'For 24 hours',
  'nt.dur.forever': 'Until I turn it back on',
  'nt.until': 'Muted until {time}',
  'nt.untilOn': 'Until you turn it back on',
  'nt.withServer': 'The server is muted',
  'nt.withCategory': 'Its category is muted',
  'nt.muted': 'muted',
  // the menus and the bell
  'nt.settings': 'Notification settings',
  'nt.bell': 'Notification settings',
  'nt.bellMuted': 'Notification settings (muted)',
  // Server settings > Overview
  'nt.def.label': 'Default notification settings',
  'nt.def.help':
    'What members get until they choose their own. With a busy server, Only @mentions keeps it calm.',
  // Settings > Notifications
  'nt.set.group': 'Server notifications',
  'nt.set.open': 'Notification settings',
  // the dialog
  'nt.dlg.muteRow': 'Mute {name}',
  'nt.dlg.muteSub':
    'No unread dots or notifications from this server. You still get mentions that your settings allow.',
  'nt.dlg.muteFor': 'Mute for',
  'nt.dlg.level': 'Server notification settings',
  'nt.dlg.levelSub': 'Following the server default: {level}',
  'nt.dlg.notifyAbout': 'Notify me about',
  'nt.dlg.suppressEveryone': 'Suppress @everyone and @here',
  'nt.dlg.suppressEveryoneSub': 'These mentions raise no badge and send no notification.',
  'nt.dlg.suppressRoles': 'Suppress all role @mentions',
  'nt.dlg.suppressRolesSub': 'Mentions of your roles raise no badge and send no notification.',
  'nt.dlg.overrides': 'Notification overrides',
  'nt.dlg.overridesSub':
    'Choose a category or channel to give it its own level or mute. A channel follows its category unless it has its own.',
  'nt.dlg.add': 'Add a category or channel',
  'nt.dlg.addPick': 'Choose…',
  'nt.dlg.none': 'No overrides yet.',
  'nt.dlg.category': 'Category',
  'nt.dlg.channelIn': 'In {name}',
  'nt.dlg.levelOf': 'Notifications for {name}',
  'nt.dlg.muteOf': 'Mute {name}',
  'nt.dlg.remove': 'Remove override for {name}',
  'nt.dlg.done': 'Done',

  // ---- the OS notifications (2026-11-01) ----
  // the toasts
  'nt.inChannel': '{who} in #{channel}',
  'nt.replyIn': '{who} replied to you in #{channel}',
  'nt.private': 'New message from {who}',
  'nt.privateMany': { one: 'New message from {who}', other: '{n} new messages from {who}' },
  'nt.count': '{title} ({n})',
  'nt.attachment': 'Sent an attachment',
  'nt.friendRequest': 'Sent you a friend request',
  'nt.call': 'Incoming call',
  // the prompt before the browser's own (web)
  'nt.ask.title': 'Turn on desktop notifications',
  'nt.ask.body':
    'Get a notification when someone messages you, mentions you or calls, even while this tab is in the background.',
  'nt.ask.allow': 'Turn on',
  'nt.ask.later': 'Not now',
  // Settings > Notifications (kept on this device)
  'nt.set.sounds': 'Notification sounds',
  'nt.set.soundsSub': 'Play a sound with each notification.',
  'nt.set.preview': 'Show message preview',
  'nt.set.previewSub': 'Off: a notification says who wrote, not what.',
  'nt.set.badge': 'Taskbar badge',
  'nt.set.badgeSub': 'The number of mentions and unread messages on the taskbar icon.',
  'nt.set.badgeWeb': 'Tab badge',
  'nt.set.badgeWebSub': "The number of mentions and unread messages in the tab's title and icon.",
  'nt.set.flash': 'Flash taskbar',
  'nt.set.flashSub': 'Flash the taskbar icon for direct messages, calls and pokes.',
  'nt.set.flashWeb': 'Flash the tab',
  'nt.set.flashWebSub': "Flash the tab's title for direct messages, calls and pokes.",
};
